/**
 * Company list row with utilization chip, nested projects, archive / restore / delete.
 * Used on G-Admin `/projects` (company picker).
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Archive, ArchiveRestore, BarChart3, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { bundeslandLabel } from "@/lib/format";
import { describeError } from "@/lib/errors";
import { liveFromState } from "@/lib/live-back";
import { useCompanyLifecycle } from "@/lib/tenancy";
import type { CompanySummary } from "@/shared/schemas/tenancy";

/** Year label for nested project rows (PRD: no fiscal_year — use startDate year). */
function projectYearLabel(startDate: string): string {
  const year = startDate.slice(0, 4);
  return /^\d{4}$/.test(year) ? year : "—";
}

type Pending = { action: "archive" | "delete" } | null;

export function CompanyRow({
  company,
  /** Primary navigation — `/companies/:id` from the projects picker. */
  selectTo,
}: {
  company: CompanySummary;
  selectTo: string;
}) {
  const { t } = useTranslation();
  const lifecycle = useCompanyLifecycle();
  const [pending, setPending] = useState<Pending>(null);

  function run(action: "archive" | "restore" | "delete", messageKey: string) {
    lifecycle.mutate(
      { id: company.id, action },
      {
        onSuccess: () => {
          toast.success(t(messageKey));
          setPending(null);
        },
        onError: (error) => toast.error(describeError(error)),
      },
    );
  }

  return (
    <div
      className={`rounded-xl border border-border bg-card p-5 shadow-soft ${company.archived ? "opacity-60" : ""}`}
    >
      <div className="flex items-center justify-between gap-3">
        {company.archived ? (
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 truncate font-medium">
              {company.name}
              <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                {t("companiesExtra.archivedBadge")}
              </span>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {bundeslandLabel(company.bundesland)}
            </div>
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Link
                to={selectTo}
                state={liveFromState("/projects")}
                className="truncate font-medium hover:underline"
              >
                {company.name}
              </Link>
              <Link
                to={`/companies/${company.id}?tab=utilization`}
                state={liveFromState("/projects")}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground transition hover:border-foreground/40 hover:text-foreground"
              >
                <BarChart3 className="size-3" />
                {t("companiesExtra.utilizationOverview")}
              </Link>
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {bundeslandLabel(company.bundesland)} · {company.projectCount}{" "}
              {t("dashboardExtra.projects", { count: company.projectCount })}
            </div>
          </div>
        )}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {!company.archived && (
            <Button variant="outline" size="sm" asChild>
              <Link to={selectTo} state={liveFromState("/projects")}>
                {t("common.open")}
              </Link>
            </Button>
          )}
          {company.archived ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={lifecycle.isPending}
              onClick={() => run("restore", "companiesExtra.restored")}
              title={t("companiesExtra.restoreTitle")}
            >
              <ArchiveRestore className="size-4" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPending({ action: "archive" })}
              title={t("companiesExtra.archiveConfirm")}
            >
              <Archive className="size-4" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => setPending({ action: "delete" })}
            title={t("companiesExtra.hardDeleteConfirm")}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
      {!company.archived && company.projects.length > 0 && (
        <ul className="mt-3 space-y-1 border-l border-border pl-3">
          {company.projects.map((project) => (
            <li key={project.id}>
              <Link
                to={`/projects/${project.id}/live`}
                state={liveFromState(`/companies/${company.id}?tab=projects`)}
                className="group flex items-center justify-between gap-3 rounded-md px-2 py-1 text-sm hover:bg-surface"
              >
                <span className="truncate group-hover:underline">{project.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {projectYearLabel(project.startDate)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={pending?.action === "archive"}
        onOpenChange={(open) => !open && setPending(null)}
        title={t("companiesExtra.archiveTitle")}
        body={t("companiesExtra.archiveBody", { name: company.name })}
        confirmLabel={t("companiesExtra.archiveConfirm")}
        busy={lifecycle.isPending}
        onConfirm={() => run("archive", "companiesExtra.archived")}
      />
      <ConfirmDialog
        open={pending?.action === "delete"}
        onOpenChange={(open) => !open && setPending(null)}
        title={t("companiesExtra.hardDeleteTitle")}
        body={t("companiesExtra.hardDeleteBodyPlain", { name: company.name })}
        confirmLabel={t("companiesExtra.hardDeleteConfirm")}
        typedConfirmation={t("projectForm.deleteConfirm")}
        typedHint={t("projectForm.deleteConfirmHint")}
        destructive
        busy={lifecycle.isPending}
        onConfirm={() => run("delete", "companiesExtra.hardDeleted")}
      />
    </div>
  );
}
